import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Info, Database, Activity, Lock, Mail, ChevronRight } from 'lucide-react';

export default function PrivacyPolicy() {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', title: '1. Introduction', icon: <Info size={18} /> },
    { id: 'data-collect', title: '2. Data We Collect', icon: <Database size={18} /> },
    { id: 'data-use', title: '3. How We Use Your Data', icon: <Activity size={18} /> },
    { id: 'security', title: '4. Data Security', icon: <Lock size={18} /> },
    { id: 'contact', title: '5. Contact Us', icon: <Mail size={18} /> }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-deep)',
      paddingTop: '140px',
      paddingBottom: '100px',
      color: 'var(--text-light)',
      fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px'
      }}>
        {/* Header Area */}
        <div className="animate-slide-up" style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'var(--primary-gold-trans)',
            marginBottom: '24px',
            border: '1px solid var(--primary-gold-border)'
          }}>
            <Shield size={40} className="text-gold" />
          </div>
          <h1 className="font-serif text-gold" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', marginBottom: '16px', lineHeight: '1.2' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '40px',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          
          {/* Sidebar Navigation */}
          <div className="glass-card animate-slide-up" style={{
            flex: '1 1 300px',
            position: 'sticky',
            top: '120px',
            padding: '30px',
            animationDelay: '0.1s'
          }}>
            <h3 className="font-serif text-gold" style={{ fontSize: '1.25rem', marginBottom: '20px', borderBottom: '1px solid var(--primary-gold-border)', paddingBottom: '15px' }}>
              Contents
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sections.map((section) => (
                <li key={section.id}>
                  <button 
                    onClick={() => scrollToSection(section.id)}
                    style={{
                      background: activeSection === section.id ? 'var(--primary-gold-trans)' : 'transparent',
                      border: '1px solid',
                      borderColor: activeSection === section.id ? 'var(--primary-gold-border)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      color: activeSection === section.id ? 'var(--primary-gold)' : 'var(--text-muted)',
                      transition: 'var(--transition-fast)',
                      fontWeight: activeSection === section.id ? '600' : '500',
                      fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => {
                      if (activeSection !== section.id) {
                        e.currentTarget.style.backgroundColor = 'var(--bg-panel)';
                        e.currentTarget.style.color = 'var(--text-light)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (activeSection !== section.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-muted)';
                      }
                    }}
                  >
                    <span style={{ opacity: activeSection === section.id ? 1 : 0.7 }}>{section.icon}</span>
                    <span style={{ flex: 1 }}>{section.title}</span>
                    {activeSection === section.id && <ChevronRight size={16} />}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Content Area */}
          <div className="privacy-section" style={{ flex: '3 1 600px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
            
            {/* 1. Introduction */}
            <div id="intro" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.2s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Info className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>1. Introduction</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                At <strong>Mystic Routes</strong>, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website (regardless of where you visit it from) and tell you about your privacy rights and how the law protects you. We believe in complete transparency when it comes to your information.
              </p>
            </div>

            {/* 2. Data We Collect */}
            <div id="data-collect" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.3s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Database className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>2. Data We Collect</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '20px' }}>
                We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                {[
                  { title: 'Identity Data', desc: 'First name, last name, username or similar identifier.' },
                  { title: 'Contact Data', desc: 'Email address, billing address, and telephone numbers.' },
                  { title: 'Technical Data', desc: 'Internet protocol (IP) address, login data, browser type and version.' },
                  { title: 'Usage Data', desc: 'Information about how you use our website, products and services.' }
                ].map((item, idx) => (
                  <div key={idx} style={{ 
                    padding: '20px', 
                    background: 'rgba(255, 255, 255, 0.4)', 
                    borderRadius: '12px',
                    border: '1px solid rgba(181, 137, 37, 0.15)'
                  }}>
                    <strong style={{ display: 'block', color: 'var(--text-light)', marginBottom: '8px', fontSize: '1.1rem' }}>{item.title}</strong>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.6' }}>{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. How We Use Your Data */}
            <div id="data-use" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.4s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Activity className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>3. How We Use Your Data</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '20px' }}>
                We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
              </p>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                {[
                  'Where we need to perform the contract we are about to enter into or have entered into with you (e.g., booking a retreat).',
                  'Where it is necessary for our legitimate interests and your interests and fundamental rights do not override those interests.',
                  'Where we need to comply with a legal or regulatory obligation.'
                ].map((text, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    color: 'var(--text-muted)',
                    lineHeight: '1.7'
                  }}>
                    <div style={{ marginTop: '6px', color: 'var(--primary-gold)' }}>
                      <ChevronRight size={16} />
                    </div>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 4. Data Security */}
            <div id="security" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.5s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Lock className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>4. Data Security</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorized way, altered or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions and they are subject to a duty of confidentiality.
              </p>
            </div>

            {/* 5. Contact Us */}
            <div id="contact" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.6s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Mail className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>5. Contact Us</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '24px' }}>
                If you have any questions about this privacy policy or our privacy practices, please feel free to reach out to our dedicated support team:
              </p>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '15px',
                background: 'var(--primary-gold-trans)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px dashed var(--primary-gold-border)'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--primary-gold)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Email Support</span>
                  <a href="mailto:privacy@mysticroutes.com" style={{ fontSize: '1.1rem', color: 'var(--text-light)', fontWeight: '500', textDecoration: 'none' }}>privacy@mysticroutes.com</a>
                </div>
                <div style={{ width: '100%', height: '1px', background: 'var(--primary-gold-border)' }}></div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--primary-gold)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Registered Address</span>
                  <address style={{ fontSize: '1.05rem', color: 'var(--text-light)', fontStyle: 'normal', lineHeight: '1.6' }}>
                    2nd Floor, Lotus Paradise Plaza,<br/>
                    Bendorwell, Bendoor,<br/>
                    Mangaluru, Karnataka 575002
                  </address>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .privacy-section .glass-card { padding: 20px 16px !important; }
        }
      `}</style>
    </div>
  );
}
